import { useEffect, useState } from "react";
import { Input, Table } from "reactstrap";
import useToggle from "../../../../hook/useToggle";
import { CancelIcon, CheckMarkIcon, EditIcon } from "../../../common/icons";
import { IAddressUsage } from "../../../../types/contact";
import AddressUpdate from "./address-update";
import { GET_ORG_ADDRESSES, SET_ORG_MAILING_ADDRESS, SET_ORG_PRIMARY_ADDRESS } from "../../../../graphql/contact-gql";
import { useLazyQuery, useMutation } from "@apollo/client";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { CheckGroup } from "../../../../hook/useCheckGroup";
import { notifySuccess } from "../../../../services/notification";
import { IOrg } from "../../../../types/org";
import ImgButton from "../../../common/img-button/img-button";
import useAuth from "../../../../hook/useAuth";
import { CategoryType } from "../../../../types/constants";
import { useFrench } from "../../../french/french-context";

type OrgAddressProps = {
    org: IOrg;
    checkGroup: CheckGroup<number>;
};

const OrgAddress = (props: OrgAddressProps ) => {
    const {org, checkGroup} = props
    const {isOpen: editAddressOpen, toggle: toggleEditAddress} = useToggle()
    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench();
    const [orgAddress, setOrgAddress] = useState<IAddressUsage>()
    // track if the on-site and mailing address checkboxes get checked by the user, not if they are already checked when loaded
    const [hasSetOnSiteAddress, setOnSiteAddress] = useState<Boolean>()
    const [hasSetAsMailingAddress, setAsMailingAddress] = useState<Boolean>()

    const [getOrgAddresses, {loading, data}] = useLazyQuery(GET_ORG_ADDRESSES, {
        onCompleted: (data) => {
            if (data?.org?.addressUsages) {
                checkGroup.init(data?.org?.addressUsages.filter((e: IAddressUsage) => !e.primary).map((e: IAddressUsage) => e.id))
            }
        }
    })
    const [setPrimaryAddress, {loading: loadingPrimary}] = useMutation(SET_ORG_PRIMARY_ADDRESS)
    const [setMailingAddress, {loading: loadingMailing}] = useMutation(SET_ORG_MAILING_ADDRESS)

    useEffect(() => {
        if (org) {
            getOrgAddresses({variables: { orgId: org.id },fetchPolicy: 'network-only'})
        }
    }, [org])

    const editAddress = (address: IAddressUsage) => {
        setOrgAddress(address)
        toggleEditAddress()
    }

    const setPrimary = (address: IAddressUsage) => {
        setPrimaryAddress({
            variables: {
                orgId: org.id,
                addressUsageId: address.id
            },
            refetchQueries: [{
                query: GET_ORG_ADDRESSES,
                fetchPolicy: 'network-only',
                variables: { orgId: address.businessEntityId }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => data && notifySuccess()
        }).then(() => {
            setOnSiteAddress(true);
        })
    }

    const setMailing = (address: IAddressUsage) => {
        setMailingAddress({
            variables: {
                orgId: org.id,
                addressUsageId: address.id
            },
            refetchQueries: [{
                query: GET_ORG_ADDRESSES,
                fetchPolicy: 'network-only',
                variables: { orgId: address.businessEntityId }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => data && notifySuccess()
        }).then(() => {
            setAsMailingAddress(true);
        });
    }

    const visibleColumnsCount = () : number => {
        // french translator won't see the select or edit columns
        return isFrenchTranslator ? 6 : 8;
    }

    return (
        <div>
            {(loading || loadingPrimary || loadingMailing) && <SpinnerOverlay />}

            {data?.org?.addressUsages && (
                <Table striped id="org_addresses" className="fixed">
                    <thead>
                        <tr>
                           {!isFrenchTranslator && <th className="icon-column">
                                <span className="sr-only">select address</span>
                                { data.org.addressUsages.length > 0 && !checkGroup.isEmpty()  &&
                                    <>
                                       <span id="orgDigitalSelectAllLbl" className="sr-only">Select all</span>
                                        <Input
                                            type="checkbox"
                                            title="Select all"
                                            aria-labelledby="orgDigitalSelectAllLbl"
                                            checked={checkGroup.isAllChecked()}
                                            onChange={checkGroup.onCheckAll}
                                            disabled={!!org.details?.shareLinkId}
                                        />
                                    </>
                                }

                            </th>}
                            <th>Organization Address</th>
                            <th>City</th>
                            <th className="column-7rem">Postal Code</th>
                            <th className="primary-mailing-column">On-Site</th>
                            <th className="primary-mailing-column">Mailing</th>
                            { (!isFrenchTranslator ||  frenchState.isFrenchChecked) && <th className="edit-column">Edit</th>}
                        </tr>
                    </thead>
                    <tbody>
                    {
                        data.org.addressUsages.length === 0
                        ?
                        <tr><td colSpan={!isFrenchTranslator? 7 : isFrenchTranslator && frenchState.isFrenchChecked?6: 5} style={{textAlign: 'center'}}>No organization addresses found.</td></tr>
                        :
                        data.org.addressUsages.map(
                            (address: IAddressUsage, index: number) => (
                                <tr key={address.id}>
                                      {!isFrenchTranslator && <td>
                                        {
                                       ( !address.primary ||   (address.primary && org.details?.category.type !== CategoryType.ORGANIZATION)) &&
                                        <Input type="checkbox"
                                            aria-label={'Select ' + address.addressTxt}
                                            checked={checkGroup.isChecked(address.id)}
                                            onChange={event=>checkGroup.onCheck(event, address.id)}
                                            disabled={!!org.details?.shareLinkId}
                                        />
                                        }
                                    </td>}
                                    <td>{address.addressTxt}</td>
                                    <td>{address.city}</td>
                                    <td>{address.postalCode}</td>

                                    {!isFrenchTranslator && <td>
                                        {address.primary
                                            ? <CheckMarkIcon title="Selected"></CheckMarkIcon>
                                            : <Input type="checkbox"
                                                aria-label="Set on-site address"
                                                checked={address.primary}
                                                onChange={()=>setPrimary(address)}
                                                disabled={!!org.details?.shareLinkId}/>
                                        }
                                        <span className="sr-only" aria-live="assertive">{address.primary && hasSetOnSiteAddress ? "checked" : "" }</span>
                                    </td>}
                                    {isFrenchTranslator && <td>
                                        {address.primary
                                            ? <CheckMarkIcon title="Selected"></CheckMarkIcon>
                                            : <CancelIcon></CancelIcon>
                                        }
                                        </td>}
                                        {!isFrenchTranslator && <td>
                                        {address.mailing
                                            ? <CheckMarkIcon title="Selected"></CheckMarkIcon>
                                            : <Input type="checkbox"
                                                aria-label="Set mailing address"
                                                checked={address.mailing}
                                                onChange={()=>setMailing(address)}
                                                disabled={!!org.details?.shareLinkId}/>
                                        }
                                        <span className="sr-only" aria-live="assertive">{address.mailing && hasSetAsMailingAddress ? "checked" : "" }</span>
                                        </td>
                                       }
                                       {isFrenchTranslator && <td>
                                        {address.mailing
                                            ? <CheckMarkIcon title="Selected"></CheckMarkIcon>
                                            : <CancelIcon></CancelIcon>
                                        }
                                        </td>}

                                    { (!isFrenchTranslator || (isFrenchTranslator && frenchState.isFrenchChecked)  )&& <td>
                                        <ImgButton className="float-end" aria-label="Edit address" title="Edit address" onClick={()=>editAddress(address)} disabled={!!org.details?.shareLinkId}>
                                            <EditIcon className="edit-pencil"/>
                                        </ImgButton>
                                    </td>}
                                </tr>
                            )
                        )}
                    </tbody>
                </Table>
            )}

            <AddressUpdate addressUsage={orgAddress} isOpen={editAddressOpen} toggle={toggleEditAddress}/>
        </div>
    )
}

export default OrgAddress;
